import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/material/styles'
import { useTracks, VideoTrack, useParticipants, RoomContext } from '@livekit/components-react'
import { Track, RoomEvent, TrackPublication } from 'livekit-client'
import {
  BsDisplay,
  BsX,
  BsFullscreenExit,
  BsChevronRight,
  BsPeopleFill,
  BsChatDots,
} from 'react-icons/bs'
import {
  VideoControlButton,
  MicOnIcon,
  MicOffIcon,
  CameraOnIcon,
  CameraOffIcon,
} from './ScreenShare/VideoControlButton'
import { useSpeakingParticipants } from '../hooks/useSpeakingParticipants'
import FloatingMessages, { FloatingMessage } from './ScreenShare/FloatingMessages'
import ParticipantsPanel from './ScreenShare/ParticipantsPanel'
import RaiseHandButton from './ScreenShare/RaiseHandButton'
import { handBadgeProps } from './ScreenShare/HandRaisedIndicator'
import { useColyseusRoom } from '../hooks/useColyseusRoom'
import { useSharedRaisedHands } from './LiveKitVideoDisplay/context/RaisedHandsContext'
import { getPhaserGameSync } from '../PhaserGame'
import Game from '../scenes/Game'
import { Message } from '../../../types/Messages'
import type { ReactionBroadcastPayload } from '../../../types/reactions'
import inputController, { InputLockReason } from '../services/InputController'
import { phaserEvents } from '../events/EventCenter'
import ChatSidebarComponent from './ScreenShare/ChatSidebar'
import { ReactionOverlay, ReactionBar, type Reaction } from './ScreenShare/Reactions'
import { virtualRoomAreaService } from '../services/VirtualRoomAreaService'
import { useVirtualRoomFilteredParticipants } from '../hooks/useVirtualRoomFilteredParticipants'
import { useAppSelector } from '../hooks'
import { useCameraControlState } from '../hooks/useCameraControlState'
import { selectCurrentVirtualRoom, selectRoomOwner } from '../stores/VirtualRoomFilterStore'
import { addNotification } from '../stores/NotificationStore'
import { screenshareChatStore, ScreenShareChatMessage } from '../stores/ScreenShareChatStore'
import { LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT } from './LiveKitRoomConnector'
import { LIVEKIT_SCREEN_SHARE_STATE_CHANGED } from './LiveKitVideoDisplay/styles/LiveKitVideoDisplay.styles'
import { shouldUseCanonicalScreenShareSessionId } from './LiveKitRoomConnectorUtils'
import { DeviceSelectMenu } from './DeviceSelectMenu'
import { MediaControlGroup } from './styles/MediaControlGroup.styles'
import { getEscapeCleanupActions } from './ScreenShare/screenShareEscapeCleanup'
import PictureInPictureAlt from '@mui/icons-material/PictureInPictureAlt'
import type { PictureInPictureSurface } from './LiveKitVideoDisplay/types/LiveKitVideoDisplay.types'
import { pipButtonState } from './GameMenu/callControlLabels'
import { SEVV_PRIMARY_CSS } from '../theme/sevvDialogChrome'

// Define z-index constants for the modal
const Z_INDEX = {
  modal: 2000,
}

// Add view mode enum
enum CameraViewMode {
  SINGLE_COLUMN = 'single',
  CONFERENCE = 'conference',
}

// Reuse DisplayIcon or keep local (unchanged)
const DisplayIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <div
    style={{
      color: 'white',
      filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.3))',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {React.createElement(BsDisplay as any, { size })}
  </div>
)

// Define interface for the screen share modal maximized state
export interface MaximizedScreenShareState {
  participantId: string
  trackSid: string
  screenShareSessionId: string
}

// Define props interface for the ScreenShareModal component
export interface ScreenShareModalProps {
  maximizedScreenShare: MaximizedScreenShareState
  onClose: () => void
  onToggleScreenShare: () => void
  screenShareEnabled: boolean
  // Add audio/video controls
  toggleAudio: () => void
  toggleVideo: () => void
  audioEnabled: boolean
  videoEnabled: boolean
  // Add switch screen share function
  onSwitchScreenShare?: (participantId: string, trackSid: string, screenShareSessionId?: string) => void
  // Change screen share source without closing the modal
  onChangeScreenShareSource?: () => void
  /**
   * SC-209: this view covers the call bar, so it carries its own pop-out button. Absent (or `mode: 'none'`)
   * means no button.
   */
  pictureInPicture?: PictureInPictureSurface
}

// Styled components for the modal
const MaximizedScreenShareContainer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: Z_INDEX.modal,
  backgroundColor: '#000',
  animation: 'fadeIn 0.2s ease-in-out',
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
})

const MaximizedScreenShareContent = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
})

const ScreenShareView = styled('div')({
  flex: 1,
  height: '100%',
  position: 'relative',
  '& > div': {
    width: '100%',
    height: '100%',
  },
})

// (Participants grid styled components moved to ParticipantsPanel)

// Add controls container for camera videos
const CameraControlsContainer = styled('div')({
  position: 'absolute',
  bottom: '8px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '6px',
  padding: '4px',
  borderRadius: '12px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 10,
})

const ParticipantName = styled('div')({
  position: 'absolute',
  bottom: '8px',
  left: '8px',
  color: 'white',
  fontSize: '12px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  padding: '4px 8px',
  borderRadius: '4px',
  zIndex: 1,
  backdropFilter: 'blur(2px)',
  fontWeight: 500,
})

const MaximizedScreenShareControls = styled('div')({
  position: 'absolute',
  bottom: '24px',
  right: '24px',
  display: 'flex',
  gap: '12px',
  zIndex: 10,
})

const ExitFullscreenButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
})

const ParticipantCountDisplay = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 12px',
  fontSize: '14px',
  fontWeight: '500',
  backdropFilter: 'blur(4px)',
  minWidth: 'fit-content',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
})

const ParticipantCountIcon = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.9)',
})

const ParticipantCountText = styled('span')({
  color: 'white',
  fontSize: '14px',
  fontWeight: '500',
  whiteSpace: 'nowrap',
})

const CloseButton = styled('button')({
  position: 'absolute',
  top: '16px',
  right: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 10,
  transition: 'background-color 0.2s, opacity 0.2s',
  opacity: 0.8,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    opacity: 1,
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
})

const ScreenShareTitle = styled('div')({
  position: 'absolute',
  top: '16px',
  left: '16px',
  color: 'white',
  fontSize: '14px',
  fontWeight: 500,
  padding: '6px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '8px',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backdropFilter: 'blur(4px)',
})

// Add scroll indicator styled component
const ScrollIndicator = styled('div')<{ show: boolean }>(({ show }) => ({
  position: 'absolute',
  bottom: '0',
  left: '0',
  right: '0',
  height: '20px',
  background: show ? 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))' : 'transparent',
  pointerEvents: 'none',
  transition: 'background 0.3s ease',
  zIndex: 10,
}))

// ChatMessage type alias from persistent store
type ChatMessage = ScreenShareChatMessage

type ColyseusRoomForChatResolve = {
  sessionId: string
  state?: { players?: Map<string, { userId?: string }> }
} | null

/** Colyseus session id for a chat message; matches LiveKit participant identity. */
function resolveAuthorSessionIdFromMessage(
  msg: ScreenShareChatMessage,
  colyseusRoom: ColyseusRoomForChatResolve
): string {
  if (msg.authorSessionId) return msg.authorSessionId
  if (!colyseusRoom || !msg.authorId) return ''
  const players = colyseusRoom.state?.players
  if (!players) return ''
  if (players.has(msg.authorId)) return msg.authorId
  let found = ''
  players.forEach((p, sid) => {
    if (found) return
    if (p?.userId === msg.authorId) found = sid
  })
  return found
}

// (FloatingMessage interface moved to FloatingMessages component)

// Add chat-related styled components
const ChatSidebar = styled('div')({
  width: '300px',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backdropFilter: 'blur(10px)',
  boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
})

const ChatHeader = styled('div')({
  padding: '12px 16px',
  borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backgroundColor: 'var(--sevv-bg-default, #fafafa)',
})

const ChatTitle = styled('div')({
  color: 'var(--sevv-text-primary, #424242)',
  fontWeight: '700',
  fontSize: '16px',
})

const ChatMessages = styled('div')({
  flex: 1,
  padding: '12px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  /* Hide scrollbar */
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
})

const MessageRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
})

const MessageContent = styled('div')({
  backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
  color: 'var(--sevv-text-primary, #424242)',
  borderRadius: '6px',
  padding: '6px 10px',
  fontSize: '13px',
  maxWidth: '200px',
  wordBreak: 'break-word',
  border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
})

const ChatInputContainer = styled('form')({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderTop: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backgroundColor: 'var(--sevv-bg-default, #fafafa)',
  gap: '8px',
})

const ChatInput = styled('input')({
  flex: 1,
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  border: '1px solid var(--sevv-divider, #eeeeee)',
  color: 'var(--sevv-text-primary, #424242)',
  fontSize: '14px',
  borderRadius: '4px',
  padding: '6px 10px',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&:focus': {
    borderColor: 'var(--sevv-primary, #f97415)',
  },
  '&::placeholder': {
    color: 'var(--sevv-gray-400, #bdbdbd)',
  },
})

const ChatToggleButton = styled(VideoControlButton)({
  width: '40px',
  height: '40px',
  backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 95%, transparent)',
  backdropFilter: 'blur(8px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 16px color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent), 0 0 0 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
  transition: 'all 0.3s ease',
  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  '@keyframes pulse': {
    '0%, 100%': {
      boxShadow: '0 4px 16px color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent), 0 0 0 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
    },
    '50%': {
      boxShadow: '0 4px 20px color-mix(in srgb, var(--sevv-primary, #f97415) 70%, transparent), 0 0 0 8px color-mix(in srgb, var(--sevv-primary, #f97415) 0%, transparent)',
    },
  },
  '&:hover': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 100%, transparent)',
    transform: 'scale(1.1)',
    boxShadow: '0 6px 24px color-mix(in srgb, var(--sevv-primary, #f97415) 80%, transparent), 0 0 0 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '& svg': {
    color: '#ffffff',
  },
})

// (Floating message styled components moved)

// Add keyboard capture layer
const KeyboardCaptureLayer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'transparent',
  zIndex: 1999, // Just below the modal
  pointerEvents: 'none', // Allow clicking through
})

// Add emoji picker styles
const EmojiPickerContainer = styled('div')<{ show: boolean }>(({ show }) => ({
  position: 'absolute',
  bottom: '64px',
  left: '12px',
  right: '12px',
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
  borderRadius: '12px',
  padding: '16px',
  display: show ? 'block' : 'none',
  zIndex: 20,
  maxHeight: '240px',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(12px)',
  /* Hide scrollbar */
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}))

const EmojiSection = styled('div')({
  marginBottom: '20px',
  '&:last-child': {
    marginBottom: '0',
  },
})

const EmojiGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: '6px',
  alignItems: 'center',
  justifyItems: 'center',
  padding: '8px 4px',
})

const EmojiButton = styled('button')({
  background: 'none',
  border: 'none',
  fontSize: '18px',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)',
    transform: 'scale(1.1)',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  },
  '&:active': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 25%, transparent)',
    transform: 'scale(1.05)',
  },
})

const EmojiCategory = styled('div')({
  color: 'var(--sevv-primary, #f97415)',
  fontSize: '11px',
  fontWeight: '700',
  margin: '0 0 12px 4px',
  textAlign: 'left',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  opacity: 0.9,
  borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  paddingBottom: '6px',
})

// Participants list drawer styles
const ParticipantsDrawer = styled('div')<{ open: boolean }>(({ open }) => ({
  position: 'absolute',
  bottom: '80px',
  right: '24px',
  width: '300px',
  maxHeight: '400px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(12px)',
  transform: open ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
  opacity: open ? 1 : 0,
  pointerEvents: open ? 'auto' : 'none',
  transition: 'all 0.2s ease',
  zIndex: 15,
  display: 'flex',
  flexDirection: 'column',
}))

const ParticipantsDrawerHeader = styled('div')({
  padding: '16px',
  borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backgroundColor: 'var(--sevv-bg-default, #fafafa)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '12px 12px 0 0',
})

const ParticipantsDrawerTitle = styled('div')({
  color: 'var(--sevv-text-primary, #424242)',
  fontSize: '16px',
  fontWeight: '700',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const ParticipantsDrawerClose = styled('button')({
  background: 'none',
  border: 'none',
  color: 'var(--sevv-text-secondary, #757575)',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)',
    color: 'var(--sevv-primary, #f97415)',
  },
})

const ParticipantsDrawerList = styled('div')({
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
  /* Hide scrollbar */
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
})

const ParticipantItem = styled('div')<{ isLocal?: boolean; isSpeaking?: boolean }>(({ isLocal, isSpeaking }) => ({
  padding: '12px 14px',
  marginBottom: '4px',
  borderRadius: '8px',
  backgroundColor: isLocal ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)' : 'var(--sevv-bg-default, #fafafa)',
  border: isSpeaking ? '2px solid color-mix(in srgb, var(--sevv-primary, #f97415) 60%, transparent)' : '1px solid var(--sevv-divider, #eeeeee)',
  boxShadow: isSpeaking ? '0 0 8px color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)' : 'none',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '&:hover': {
    backgroundColor: isLocal ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)' : 'var(--sevv-bg-paper, #ffffff)',
    borderColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 35%, transparent)',
  },
}))

const ParticipantAvatar = styled('div')<{ isLocal?: boolean }>(({ isLocal }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: isLocal ? 'var(--sevv-primary, #f97415)' : 'var(--sevv-gray-400, #bdbdbd)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '14px',
  fontWeight: '600',
  flexShrink: 0,
  border: isLocal ? '2px solid color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)' : 'none',
}))

const ParticipantInfo = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  overflow: 'hidden',
})

const ParticipantNameText = styled('div')({
  color: 'var(--sevv-text-primary, #424242)',
  fontSize: '14px',
  fontWeight: '500',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const ParticipantBadge = styled('span')<{ type: 'local' | 'speaking' | 'owner' | 'hand-raised' }>(({ type }) => ({
  fontSize: '11px',
  fontWeight: '600',
  padding: '2px 6px',
  borderRadius: '4px',
  backgroundColor:
    type === 'local' ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)' :
    type === 'owner' ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)' :
    type === 'hand-raised' ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)' :
    'color-mix(in srgb, var(--sevv-success, #27ae60) 15%, transparent)',
  color:
    type === 'local' ? 'var(--sevv-primary, #f97415)' :
    type === 'owner' ? 'var(--sevv-primary, #f97415)' :
    type === 'hand-raised' ? 'var(--sevv-primary, #f97415)' :
    'var(--sevv-success, #27ae60)',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  display: 'inline-block',
  border:
    type === 'local' ? '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)' :
    type === 'owner' ? '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)' :
    type === 'hand-raised' ? '1px solid var(--sevv-primary, #f97415)' :
    '1px solid color-mix(in srgb, var(--sevv-success, #27ae60) 30%, transparent)',
}))

// Screen share takeover notification styles
const TakeoverNotification = styled('div')<{ show: boolean }>(({ show }) => ({
  position: 'absolute',
  top: '70px',
  left: '50%',
  transform: show ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-20px)',
  opacity: show ? 1 : 0,
  pointerEvents: show ? 'auto' : 'none',
  transition: 'all 0.3s ease',
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 95%, transparent)',
  padding: '12px 20px',
  borderRadius: '12px',
  backdropFilter: 'blur(12px)',
  boxShadow: '0 4px 24px color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  minWidth: '320px',
  maxWidth: '500px',
}))

const TakeoverNotificationContent = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const TakeoverNotificationTitle = styled('div')({
  color: 'white',
  fontSize: '14px',
  fontWeight: '600',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const TakeoverNotificationText = styled('div')({
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '12px',
})

const TakeoverNotificationButton = styled('button')({
  backgroundColor: 'white',
  color: 'color-mix(in srgb, var(--sevv-primary, #f97415) 100%, transparent)',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
})

const TakeoverNotificationClose = styled('button')({
  backgroundColor: 'transparent',
  color: 'white',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
})

// Screen share source menu (Google Meet-style dropdown)
const ScreenShareMenu = styled('div')<{ open: boolean }>(({ open }) => ({
  position: 'absolute',
  bottom: '52px',
  right: '0',
  backgroundColor: 'rgba(30, 30, 30, 0.95)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  minWidth: '180px',
  transform: open ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.97)',
  opacity: open ? 1 : 0,
  pointerEvents: open ? 'auto' : 'none',
  transition: 'transform 0.18s ease, opacity 0.18s ease',
  zIndex: 30,
}))

const ScreenShareMenuItem = styled('button')<{ danger?: boolean }>(({ danger }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '11px 16px',
  color: danger ? '#ff6b6b' : 'white',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.15s ease',
  '&:hover': {
    backgroundColor: danger ? 'rgba(220,53,69,0.2)' : 'rgba(255,255,255,0.1)',
  },
  '& + &': {
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
}))

// Screen share modal component
const ScreenShareModal: React.FC<ScreenShareModalProps> = ({
  maximizedScreenShare,
  onClose,
  onToggleScreenShare,
  screenShareEnabled,
  toggleAudio,
  toggleVideo,
  audioEnabled,
  videoEnabled,
  onSwitchScreenShare,
  onChangeScreenShareSource,
  pictureInPicture,
}) => {
  const dispatch = useDispatch()

  // SC-158: the same reading as the call bar's camera button. A camera-on made while the
  // background pipeline rebuilds is dropped by the connector, so "on" is disabled for that window.
  const { disabled: cameraDisabled, label: cameraLabel } = useCameraControlState(videoEnabled)

  // Use filtered participants and tracks from centralized hook
  const {
    participants: rawFilteredParticipants,
    screenShareTracks: screenShareTracksRaw,
    cameraTracks: filteredCameraTracks,
    microphoneTracks: filteredMicrophoneTracks,
    isParticipantInSameRoom
  } = useVirtualRoomFilteredParticipants()

  // Keep a stable fallback so the participant grid never flashes empty while
  // the proximity hook is re-computing during near-area transitions.
  const lastKnownParticipantsRef = useRef(rawFilteredParticipants)
  if (rawFilteredParticipants.length > 0) {
    lastKnownParticipantsRef.current = rawFilteredParticipants
  }
  const participants = rawFilteredParticipants.length > 0
    ? rawFilteredParticipants
    : lastKnownParticipantsRef.current

  const liveKitAllParticipants = useParticipants()
  const liveKitIdentities = useMemo(
    () => new Set(liveKitAllParticipants.map((p) => p.identity)),
    [liveKitAllParticipants]
  )

  const room = useColyseusRoom()
  const { handRaisedSessionIds, localHandRaised, toggleHandRaise, lowerHand, canRaiseHand } = useSharedRaisedHands(room)

  const currentRoom = useAppSelector(selectCurrentVirtualRoom)
  const ownerSessionId = useAppSelector((state) =>
    currentRoom ? selectRoomOwner(state, currentRoom.roomId) : undefined
  )

  const { speakingIds, speakingOrder } = useSpeakingParticipants()
  const liveKitRoom = useContext(RoomContext) // LiveKit room instance from context

  /** Drawer + count: only peers in the same virtual meeting as tracks (not whole LiveKit office). */
  const rawSharingContextParticipants = useMemo(
    () =>
      liveKitAllParticipants.filter(
        (p) => p.isLocal || isParticipantInSameRoom(p.identity)
      ),
    [liveKitAllParticipants, isParticipantInSameRoom]
  )

  const sharingContextParticipants = useMemo(() => {
    return [...rawSharingContextParticipants].sort((a, b) => {
      const isLocalA = a.isLocal
      const isLocalB = b.isLocal

      if (isLocalA && !isLocalB) return -1
      if (!isLocalA && isLocalB) return 1
      if (isLocalA && isLocalB) return 0

      const idA = a.identity
      const idB = b.identity

      const handRaisedA = room?.state?.players?.get(idA)?.handRaised ?? false
      const handRaisedB = room?.state?.players?.get(idB)?.handRaised ?? false

      if (handRaisedA && !handRaisedB) return -1
      if (!handRaisedA && handRaisedB) return 1

      const indexA = speakingOrder.indexOf(idA)
      const indexB = speakingOrder.indexOf(idB)

      const spokeA = indexA !== -1
      const spokeB = indexB !== -1

      if (spokeA && !spokeB) return -1
      if (!spokeA && spokeB) return 1

      if (spokeA && spokeB) {
        return indexA - indexB
      }

      const nameA = a.name || a.identity || ''
      const nameB = b.name || b.identity || ''
      return nameA.localeCompare(nameB)
    })
  }, [rawSharingContextParticipants, room, handRaisedSessionIds, speakingOrder])

  const sharingContextParticipantCount = sharingContextParticipants.length

  const isLocalSharerIdentity = participants.find((p) => p.isLocal)?.identity
  const isLocalSharing = maximizedScreenShare.participantId === isLocalSharerIdentity

  // Sync screen share button state when local share ends externally
  // (e.g., via native OS "stop sharing" or track ending unexpectedly).
  // The button state (screenShareEnabled) may remain true while LiveKit already
  // reports the share as stopped — dispatch a state-change event to fix the desync.
  useEffect(() => {
    if (!isLocalSharing || !screenShareEnabled) return

    const participant = participants.find(p => p.identity === maximizedScreenShare.participantId)
    if (participant && !participant.isScreenShareEnabled) {
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, { detail: { enabled: false } })
      )
    }
  }, [isLocalSharing, screenShareEnabled, participants, maximizedScreenShare.participantId])

  // Track published screen share tracks separately for event-based detection
  const [publishedScreenShareTracks, setPublishedScreenShareTracks] = useState<Map<string, TrackPublication>>(new Map())
  const [showParticipants, setShowParticipants] = useState(true)
  // participant grid scroll indicator and ref moved to ParticipantsPanel

  // Add camera view mode state
  const [cameraViewMode, setCameraViewMode] = useState<CameraViewMode>(CameraViewMode.SINGLE_COLUMN)

  // Chat state: wait for the canonical server/session id instead of silently forking chat
  const initialSessionId = shouldUseCanonicalScreenShareSessionId(maximizedScreenShare.screenShareSessionId)
    ? maximizedScreenShare.screenShareSessionId
    : ''
  const [showChat, setShowChat] = useState(false)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() =>
    initialSessionId ? screenshareChatStore.getMessages(initialSessionId) : []
  )
  const [chatInput, setChatInput] = useState('')
  const [currentRoomId, setCurrentRoomId] = useState<string>('')
  const [screenshareSessionId, setScreenshareSessionId] = useState<string>(initialSessionId)
  const chatMessagesRef = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<HTMLInputElement>(null)

  // Refs for chat broadcast handler — handler registers once and reads current values
  const chatHandlerRefs = useRef<{
    screenshareSessionId: string
    participants: typeof participants
    liveKitIdentities: Set<string>
    isParticipantInSameRoom: typeof isParticipantInSameRoom
    addFloatingMessage: (m: ChatMessage) => void
    setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
    filterMessages: (
      messages: ChatMessage[],
      sessionId: string,
      participantsList: any[],
      isInSameRoom: (id: string) => boolean,
      colyseusRoom: ColyseusRoomForChatResolve,
      liveKitIds: Set<string>
    ) => ChatMessage[]
  } | null>(null)

  // Floating messages state
  const [floatingMessages, setFloatingMessages] = useState<FloatingMessage[]>([])

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiButtonRef = useRef<HTMLButtonElement>(null)

  // Participants drawer state
  const [showParticipantsDrawer, setShowParticipantsDrawer] = useState(false)
  const [isParticipantsListLoaded, setIsParticipantsListLoaded] = useState(false)

  // Reaction bar visibility toggle
  const [showReactionBar, setShowReactionBar] = useState(false)
  const reactionButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsParticipantsListLoaded(true)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  // New screen share takeover state (auto-detected new shares)
  const [newScreenShare, setNewScreenShare] = useState<{
    participantId: string
    trackSid: string
    participantName: string
  } | null>(null)
  const newScreenShareRef = useRef(newScreenShare)
  newScreenShareRef.current = newScreenShare
  const [showTakeoverNotification, setShowTakeoverNotification] = useState(false)

  // Change Source / Stop Sharing dropdown menu state
  const [showScreenShareMenu, setShowScreenShareMenu] = useState(false)
  const screenShareMenuRef = useRef<HTMLDivElement>(null)
  // True for ~3 s while a source-change is in progress — suppresses auto-close
  const isChangingSourceRef = useRef(false)

  // Presenter takeover request/response protocol state
  const [takeoverRequestPending, setTakeoverRequestPending] = useState(false)
  const [takeoverResultMessage, setTakeoverResultMessage] = useState<string | null>(null)
  const [incomingTakeoverRequest, setIncomingTakeoverRequest] = useState<{
    requesterName: string
    requesterUserId: string
  } | null>(null)



  // Owner or presenter may lower other people's hands (server enforces the same rule)
  const localPlayer = room?.state?.players?.get(room.sessionId)
  const isLocalOwner = ownerSessionId === room?.sessionId
  const isLocalPresenter = localPlayer?.screenShareSessionId && localPlayer?.screenShareSessionId !== ''

  // Reactions state
  const [reactions, setReactions] = useState<Reaction[]>([])

  const addReaction = (emoji: string) => {
    console.log('[ScreenShare] Adding reaction:', emoji)
    const reaction: Reaction = { id: `${Date.now()}-${Math.random()}`, emoji, author: 'You' }
    setReactions((prev) => [...prev, reaction])
    setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== reaction.id)), 1600)

    // Broadcast to others via server
    if (room) {
      console.log('[ScreenShare] Broadcasting reaction to server:', emoji)
      room.send(Message.ADD_REACTION, { emoji })
    } else {
      console.warn('[ScreenShare] No room available to broadcast reaction')
    }
  }

  // Listener for reactions from others - use phaserEvents to avoid breaking Colyseus handlers
  useEffect(() => {
    console.log('[ScreenShare] Setting up reaction listener via phaserEvents')

    // Listen via phaserEvents instead of directly on room to avoid cleanup issues
    const reactionHandler = (data: ReactionBroadcastPayload) => {
      console.log('[ScreenShare] Received reaction from server:', data?.emoji)
      if (data?.emoji) {
        // Add reaction without broadcasting (to avoid infinite loop)
        const reaction: Reaction = { id: `${Date.now()}-${Math.random()}`, emoji: data.emoji, author: data.author }
        setReactions((prev) => [...prev, reaction])
        setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== reaction.id)), 1600)
      }
    }

    phaserEvents.on('screenShareReaction', reactionHandler)
    return () => {
      phaserEvents.off('screenShareReaction', reactionHandler)
    }
  }, [])

  // Screen share takeover protocol listeners
  useEffect(() => {
    const handleTakeoverRequest = (data: { requesterName: string; requesterUserId: string }) => {
      if (!isLocalSharing) return // only the presenter handles incoming requests
      setIncomingTakeoverRequest(data)
    }

    const handleTakeoverResult = (data: { accepted: boolean; presenterName?: string; reason?: string; screenShareSessionId?: string }) => {
      setTakeoverRequestPending(false)
      if (data.accepted) {
        setTakeoverResultMessage(`${data.presenterName || 'Presenter'} accepted your request. Starting screen share...`)
        // Inherit the persistent session ID so the new share continues the same session
        if (data.screenShareSessionId) {
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT, {
              detail: { sessionId: data.screenShareSessionId },
            })
          )
        }
        setTimeout(() => {
          onToggleScreenShare()
          setTakeoverResultMessage(null)
        }, 1500)
      } else {
        setTakeoverResultMessage(data.reason || 'Request was declined.')
        setTimeout(() => setTakeoverResultMessage(null), 4000)
      }
    }

    phaserEvents.on('screenShareTakeoverRequest', handleTakeoverRequest)
    phaserEvents.on('screenShareTakeoverResult', handleTakeoverResult)
    return () => {
      phaserEvents.off('screenShareTakeoverRequest', handleTakeoverRequest)
      phaserEvents.off('screenShareTakeoverResult', handleTakeoverResult)
    }
  }, [isLocalSharing, onToggleScreenShare])

  // Track blocked keys for keyboard input management (ref to avoid re-registering listeners)
  const blockedKeysRef = useRef<Set<string>>(new Set())

  // Event-based track detection: Listen to LiveKit track publication events.
  // Only depend on liveKitRoom — use participantsRef for mount-time scan to
  // avoid tearing down / re-adding listeners every 200ms.
  const participantsRef = useRef(participants)
  participantsRef.current = participants

  useEffect(() => {
    if (!liveKitRoom) return

    const handleTrackPublished = (publication: TrackPublication, participant: any) => {
      // Video only: the map holds one publication per sharer, and tab audio (ScreenShareAudio)
      // publishes after the video, so letting it in would replace the video entry.
      if (publication.source === Track.Source.ScreenShare) {
        console.log('[ScreenShare] Track published:', {
          participantId: participant.identity,
          trackSid: publication.trackSid,
          source: publication.source
        })

        // Store published track for early detection
        setPublishedScreenShareTracks(prev => {
          const newMap = new Map(prev)
          newMap.set(participant.identity, publication)
          return newMap
        })
      }
    }

    const handleTrackUnpublished = (publication: TrackPublication, participant: any) => {
      // Video only, so a tab-audio unpublish cannot drop a video share that is still live.
      if (publication.source === Track.Source.ScreenShare) {
        console.log('[ScreenShare] Track unpublished:', {
          participantId: participant.identity,
          trackSid: publication.trackSid
        })

        // Remove from published tracks
        setPublishedScreenShareTracks(prev => {
          const newMap = new Map(prev)
          newMap.delete(participant.identity)
          return newMap
        })
      }
    }

    // Listen to track publication events
    liveKitRoom.on(RoomEvent.TrackPublished, handleTrackPublished)
    liveKitRoom.on(RoomEvent.TrackUnpublished, handleTrackUnpublished)

    // Check existing publications on mount (read from ref, not dep)
    const currentParticipants = participantsRef.current
    const initialTracks = new Map<string, TrackPublication>()
    currentParticipants.forEach(participant => {
      participant.trackPublications.forEach(publication => {
        if (publication.source === Track.Source.ScreenShare) {
          initialTracks.set(participant.identity, publication)
        }
      })
    })
    if (initialTracks.size > 0) {
      setPublishedScreenShareTracks(initialTracks)
    }

    return () => {
      liveKitRoom.off(RoomEvent.TrackPublished, handleTrackPublished)
      liveKitRoom.off(RoomEvent.TrackUnpublished, handleTrackUnpublished)
    }
  }, [liveKitRoom])

  const sharerName =
    participants.find((p) => p.identity === maximizedScreenShare.participantId)?.name ||
    'Screen Share'

  // Simplified track detection: Match by participant first, trackSid as secondary
  // Use participant state as primary indicator
  const screenShareTrack = React.useMemo(() => {
    // First, find participant
    const participant = participants.find(p => p.identity === maximizedScreenShare.participantId)
    if (!participant) {
      return undefined
    }

    // Check if participant is screen sharing (primary indicator)
    if (!participant.isScreenShareEnabled) {
      return undefined
    }

    // Find track by participant - match by participant first
    let track = screenShareTracksRaw.find(t => t.participant.identity === maximizedScreenShare.participantId)

    // If trackSid is provided and doesn't match, try to find by trackSid
    const trackSid = track?.publication?.trackSid || (track?.publication?.track as any)?.sid
    if (track && maximizedScreenShare.trackSid && trackSid !== maximizedScreenShare.trackSid) {
      const trackBySid = screenShareTracksRaw.find(t => (t.publication?.trackSid || (t.publication?.track as any)?.sid) === maximizedScreenShare.trackSid)
      if (trackBySid && trackBySid.participant.identity === maximizedScreenShare.participantId) {
        track = trackBySid
      }
    }

    // If no track found in useTracks, check published tracks (event-based detection)
    if (!track && publishedScreenShareTracks.size > 0) {
      const publishedTrack = publishedScreenShareTracks.get(maximizedScreenShare.participantId)
      if (publishedTrack && publishedTrack.source === Track.Source.ScreenShare) {
        // Track is published but not yet in useTracks - synthesize a TrackReference
        return {
          participant,
          publication: publishedTrack,
          source: Track.Source.ScreenShare
        } as any
      }
    }

    // Basic validation: only check if track exists and isn't ended
    if (track) {
      const trackObj = track.publication?.track as any
      if (trackObj && trackObj.readyState === 'ended') {
        return undefined
      }
    }

    return track
  }, [maximizedScreenShare.participantId, maximizedScreenShare.trackSid, screenShareTracksRaw, participants, publishedScreenShareTracks])

  // Simplified: Get all screen share tracks - use participant state as primary filter
  const allScreenShareTracks = React.useMemo(() => {
    if (!screenShareTracksRaw || !Array.isArray(screenShareTracksRaw)) {
      return [];
    }

    // Filter by participant screen share state first
    return screenShareTracksRaw.filter((t) => {
      // Basic validation: track must exist and participant must be screen sharing
      if (!t || !t.participant) {
        return false;
      }

      // Use participant state as primary indicator
      if (!t.participant.isScreenShareEnabled) {
        return false;
      }

      // Only reject if track is explicitly ended
      const trackObj = t.publication?.track as any;
      if (trackObj && trackObj.readyState === 'ended') {
        return false;
      }

      return true;
    });
  }, [screenShareTracksRaw]);

  // Detect new screen shares from other participants (simplified).
  // Read newScreenShare from a ref to avoid a self-triggering dependency cycle.
  useEffect(() => {
    if (!onSwitchScreenShare) return

    // Find participants who are screen sharing but not the current one
    // CRITICAL: Only same-room participants - filter by virtual room membership
    const otherScreenSharingParticipants = participants.filter(
      p => p.identity !== maximizedScreenShare.participantId &&
        p.isScreenShareEnabled &&
        isParticipantInSameRoom(p.identity) // CRITICAL: Only same-room participants
    )

    // Find corresponding tracks
    // CRITICAL: Only same-room tracks - filter by virtual room membership
    const otherScreenShares = allScreenShareTracks.filter(
      t => t.participant.identity !== maximizedScreenShare.participantId &&
        isParticipantInSameRoom(t.participant.identity) // CRITICAL: Only same-room tracks
    )

    // If there's a new screen share participant or track, show notification
    if (otherScreenSharingParticipants.length > 0 || otherScreenShares.length > 0) {
      // Prefer track if available, otherwise use participant
      const newShare = otherScreenShares[0] || (otherScreenSharingParticipants[0] ? {
        participant: otherScreenSharingParticipants[0],
        trackSid: publishedScreenShareTracks.get(otherScreenSharingParticipants[0].identity)?.trackSid || ''
      } : null)

      if (newShare) {
        const participantName = newShare.participant.name || newShare.participant.identity

        // Only show notification if it's different from current (read from ref, not dep)
        const trackSid = 'trackSid' in newShare ? newShare.trackSid : (newShare.publication?.trackSid || (newShare.publication?.track as any)?.sid || '')
        const currentNewScreenShare = newScreenShareRef.current
        if (
          !currentNewScreenShare ||
          currentNewScreenShare.participantId !== newShare.participant.identity ||
          currentNewScreenShare.trackSid !== trackSid
        ) {
          setNewScreenShare({
            participantId: newShare.participant.identity,
            trackSid: trackSid,
            participantName,
          })
          setShowTakeoverNotification(true)

          // Auto-hide notification after 10 seconds
          const timer = setTimeout(() => {
            setShowTakeoverNotification(false)
          }, 10000)

          return () => clearTimeout(timer)
        }
      }
    } else {
      // No other screen shares available, clear the notification
      setShowTakeoverNotification(false)
      setNewScreenShare(null)
    }
  }, [
    allScreenShareTracks,
    participants,
    maximizedScreenShare.participantId,
    maximizedScreenShare.trackSid,
    onSwitchScreenShare,
    publishedScreenShareTracks,
    isParticipantInSameRoom,
  ])

  // Track error handling: monitor for track errors but check participant state first
  useEffect(() => {
    if (!screenShareTrack) return

    const handleTrackError = (event: any) => {
      console.error('[ScreenShare] Track error detected:', event)
      // Check participant state before closing - they might restart
      const participant = participants.find(p => p.identity === screenShareTrack.participant.identity)
      if (!participant?.isScreenShareEnabled) {
        const track = screenShareTrack.publication?.track as any
        if (track && track.readyState === 'ended') {
          console.warn('[ScreenShare] Track ended and participant not screen sharing, will close')
          // Don't close immediately - let auto-close logic handle it
        }
      } else {
        console.log('[ScreenShare] Track error but participant still screen sharing, waiting for recovery')
      }
    }

    const handleTrackEnded = () => {
      console.warn('[ScreenShare] Track ended event received')
      // Check participant state - they might have restarted
      const participant = participants.find(p => p.identity === screenShareTrack.participant.identity)
      if (!participant?.isScreenShareEnabled) {
        console.warn('[ScreenShare] Track ended and participant not screen sharing, will close')
        // Don't close immediately - let auto-close logic handle it
      }
    }

    const track = screenShareTrack.publication?.track
    // Only add event listeners if track exists and supports listeners
    // Track from LiveKit uses MediaStreamTrack events: 'ended' and 'error'
    if (track && typeof (track as any).addEventListener === 'function') {
      (track as any).addEventListener('ended', handleTrackEnded)
        (track as any).addEventListener('error', handleTrackError)

      return () => {
        if (track && typeof (track as any).removeEventListener === 'function') {
          (track as any).removeEventListener('ended', handleTrackEnded)
            (track as any).removeEventListener('error', handleTrackError)
        }
      }
    }
  }, [screenShareTrack, participants, onClose])

  // Cleanup when modal closes or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup: ensure no lingering track references
      if (screenShareTrack?.publication?.track) {
        try {
          const track = screenShareTrack.publication.track as any
          // Don't stop the track (it's managed by LiveKit), just cleanup listeners
          if (track.removeEventListener) {
            track.removeEventListener('error', () => { })
            track.removeEventListener('ended', () => { })
          }
        } catch (error) {
          console.warn('[ScreenShare] Error during cleanup:', error)
        }
      }
    }
  }, [screenShareTrack])

  // Ref to persist the auto-close timer across re-renders
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // Ref to hold the latest state for the timer closure
  const autoCloseStateRef = useRef({
    participants,
    maximizedScreenShare,
    isParticipantInSameRoom,
    onClose,
    isChangingSourceRef
  })

  // Keep the ref in sync synchronously so timer closures always read the latest values.
  autoCloseStateRef.current = {
    participants,
    maximizedScreenShare,
    isParticipantInSameRoom,
    onClose,
    isChangingSourceRef
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
      }
    }
  }, [])

  // Simplified auto-close logic: Only close when participant actually stops screen sharing
  useEffect(() => {
    // Find the participant we're displaying
    const participant = participants.find(p => p.identity === maximizedScreenShare.participantId)

    // Primary check: Is participant still screen sharing?
    const isParticipantScreenSharing = participant?.isScreenShareEnabled ?? false

    // Secondary check: Does participant still exist?
    const participantExists = !!participant

    // Filtered roster can momentarily omit someone who is still in LiveKit with an active share (AoI races).
    const lkPeer = liveKitAllParticipants.find((p) => p.identity === maximizedScreenShare.participantId)
    const stillPublishingShare =
      !!lkPeer &&
      (lkPeer.isScreenShareEnabled ||
        screenShareTracksRaw.some(
          (t) => t.participant.identity === maximizedScreenShare.participantId && t.publication?.track
        ))
    if (!participantExists && stillPublishingShare) {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
      return
    }

    // Only proceed with auto-close if participant stopped screen sharing or left
    if (!isParticipantScreenSharing || !participantExists) {
      // Check if there are any other participants screen sharing
      // CRITICAL: Only same-room participants - filter by virtual room membership
      const otherScreenSharingParticipants = participants.filter(
        p => p.identity !== maximizedScreenShare.participantId &&
          p.isScreenShareEnabled &&
          isParticipantInSameRoom(p.identity) // CRITICAL: Only same-room participants
      )

      // Also filter tracks by virtual room
      const hasOtherScreenShares = otherScreenSharingParticipants.length > 0 ||
        allScreenShareTracks.filter(t =>
          t.participant.identity !== maximizedScreenShare.participantId &&
          isParticipantInSameRoom(t.participant.identity)
        ).length > 0

      if (hasOtherScreenShares) {
        // Clear any pending timer if we're switching
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current)
          autoCloseTimerRef.current = null
        }

        // Switch to another screen share if available
        // CRITICAL: Only same-room tracks
        const nextTrack = allScreenShareTracks.find(
          t => t.participant.identity !== maximizedScreenShare.participantId &&
            isParticipantInSameRoom(t.participant.identity)
        ) || allScreenShareTracks.find(t => isParticipantInSameRoom(t.participant.identity))

        if (nextTrack && onSwitchScreenShare) {
          console.log('[ScreenShare] Switching to other screen share:', nextTrack.participant.identity)
          onSwitchScreenShare(nextTrack.participant.identity, nextTrack.publication?.trackSid || (nextTrack.publication?.track as any)?.sid || '', screenshareSessionId)
          return
        }
      }

      // No other screen shares - close after 10-second grace period
      // BUT skip if a source-change is in progress (the share is momentarily stopped)
      if (isChangingSourceRef.current) {
        console.log('[ScreenShare] Source change in progress, skipping auto-close')
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current)
          autoCloseTimerRef.current = null
        }
        return
      }

      // Only start the timer if it's not already running
      if (!autoCloseTimerRef.current) {
        console.log('[ScreenShare] No active screen shares, starting 10-second auto-close timer')
        autoCloseTimerRef.current = setTimeout(() => {
          autoCloseTimerRef.current = null
          const state = autoCloseStateRef.current
          
          // Skip if a source change started during the countdown
          if (state.isChangingSourceRef.current) {
            console.log('[ScreenShare] Source change detected during countdown, aborting auto-close')
            return
          }

          // Final check: verify no one is screen sharing
          const currentParticipant = state.participants.find(p => p.identity === state.maximizedScreenShare.participantId)
          const stillNotSharing = !currentParticipant?.isScreenShareEnabled

          // Check if any participant is now screen sharing (only same-room participants)
          const anyScreenSharing = state.participants.some(p =>
            p.isScreenShareEnabled &&
            (p.isLocal || state.isParticipantInSameRoom(p.identity))
          )

          if (stillNotSharing && !anyScreenSharing) {
            console.log('[ScreenShare] Confirmed no screen shares after 10s, closing modal')
            state.onClose()
          } else {
            console.log('[ScreenShare] Screen share detected within grace period, keeping modal open')
          }
        }, 10000) // 10-second grace period
      }
    } else {
      // If participant is still screen sharing, keep modal open
      // and clear any pending auto-close timer
      if (autoCloseTimerRef.current) {
        console.log('[ScreenShare] Participant resumed sharing, clearing auto-close timer')
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
    }
  }, [
    participants,
    maximizedScreenShare.participantId,
    allScreenShareTracks,
    liveKitAllParticipants,
    screenShareTracksRaw,
    onSwitchScreenShare,
    screenshareSessionId,
    isParticipantInSameRoom
  ])

  // Get camera tracks for all participants, sorted with speaking/hand-raised participants first
  // Use filtered camera tracks from hook
  const cameraTracks = filteredCameraTracks
  const sortedCameraTracks = useMemo(() => {
    const local = cameraTracks.filter((t) => t.participant.isLocal)
    const remote = cameraTracks.filter((t) => !t.participant.isLocal)

    const sortedRemote = [...remote].sort((a, b) => {
      const idA = a.participant.identity
      const idB = b.participant.identity

      const handRaisedA = room?.state?.players?.get(idA)?.handRaised ?? false
      const handRaisedB = room?.state?.players?.get(idB)?.handRaised ?? false

      if (handRaisedA && !handRaisedB) return -1
      if (!handRaisedA && handRaisedB) return 1

      const indexA = speakingOrder.indexOf(idA)
      const indexB = speakingOrder.indexOf(idB)

      const spokeA = indexA !== -1
      const spokeB = indexB !== -1

      if (spokeA && !spokeB) return -1
      if (!spokeA && spokeB) return 1

      if (spokeA && spokeB) {
        return indexA - indexB
      }

      const nameA = a.participant.name || a.participant.identity || ''
      const nameB = b.participant.name || b.participant.identity || ''
      return nameA.localeCompare(nameB)
    })

    return [...local, ...sortedRemote]
  }, [cameraTracks, room, handRaisedSessionIds, speakingOrder])

  // Helper function to group camera tracks into rows of 2 for conference mode
  const groupedCameraTracks = useMemo(() => {
    const rows: typeof sortedCameraTracks[] = []
    for (let i = 0; i < sortedCameraTracks.length; i += 2) {
      rows.push(sortedCameraTracks.slice(i, i + 2))
    }
    return rows
  }, [sortedCameraTracks])

  // Memoize sortedCameraTracks with trackSid to prevent new array references on every render
  const sortedCameraTracksWithSid = useMemo(() => {
    return sortedCameraTracks.map(t => ({
      ...t,
      trackSid: t.publication?.trackSid || (t.publication?.track as any)?.sid || ''
    })) as any
  }, [sortedCameraTracks])

  // Memoize groupedCameraTracks with trackSid to prevent new array references on every render
  const groupedCameraTracksWithSid = useMemo(() => {
    return groupedCameraTracks.map(row =>
      row.map(t => ({
        ...t,
        trackSid: t.publication?.trackSid || (t.publication?.track as any)?.sid || ''
      }))
    ) as any
  }, [groupedCameraTracks])

  // Build a fast-lookup map: participantId -> camera track (used by ParticipantsPanel
  // so tiles can be rendered for ALL participants, not just those with camera on)
  const cameraTracksByParticipantId = useMemo(() => {
    const map = new Map<string, any>()
    sortedCameraTracksWithSid.forEach((t: any) => {
      map.set(t.participant.identity, t)
    })
    return map
  }, [sortedCameraTracksWithSid])

  // Scroll indicator logic moved to ParticipantsPanel

  // Toggle participants panel
  const toggleParticipantsPanel = () => {
    setShowParticipants((prev) => !prev)
  }

  // Toggle camera view mode
  const toggleCameraViewMode = () => {
    setCameraViewMode((prev) =>
      prev === CameraViewMode.SINGLE_COLUMN
        ? CameraViewMode.CONFERENCE
        : CameraViewMode.SINGLE_COLUMN
    )
  }

  // Get current room ID
  const getCurrentRoomId = (): string => {
    try {
      const phaserGame = getPhaserGameSync()
      if (!phaserGame) return ''

      const game = phaserGame.scene?.keys?.game as Game | undefined
      if (game?.network?.room) {
        return game.network.room.roomId || ''
      }
    } catch (e) {
      // Room not available yet
    }
    return ''
  }

  // Sync screenshare session ID from props (persistent partition ID).
  // Falls back to participant-derived ID for backward compat when the server
  // hasn't assigned one yet.
  useEffect(() => {
    const sessionId = maximizedScreenShare.screenShareSessionId
      || `screenshare-${maximizedScreenShare.participantId}`
    if (sessionId && sessionId !== screenshareSessionId) {
      console.log('[ScreenShareModal] Screenshare session ID:', sessionId)
      setScreenshareSessionId(sessionId)
      screenshareChatStore.setActiveSession(sessionId)
      const restored = screenshareChatStore.getMessages(sessionId)
      setChatMessages(restored)
      setFloatingMessages([])
    }
  }, [maximizedScreenShare.screenShareSessionId, maximizedScreenShare.participantId, screenshareSessionId])

  // Update room ID when room changes (for sending messages, but not for filtering)
  useEffect(() => {
    const roomId = getCurrentRoomId()
    if (roomId && roomId !== currentRoomId) {
      setCurrentRoomId(roomId)
    }
  }, [room, currentRoomId])

  // Dismiss floating message function
  const dismissFloatingMessage = React.useCallback((messageId: string) => {
    setFloatingMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, dismissed: true } : msg))
    )

    // Remove from DOM after animation
    setTimeout(() => {
      setFloatingMessages((prev) => prev.filter((msg) => msg.id !== messageId))
    }, 300)
  }, [])

  // Add floating message helper function
  const addFloatingMessage = React.useCallback(
    (message: ChatMessage) => {
      console.log(
        '[ScreenShareModal] addFloatingMessage called with:',
        message,
        'showChat:',
        showChat
      )

      if (showChat) {
        console.log('[ScreenShareModal] Chat sidebar is open, skipping floating message')
        return // Don't show floating messages when sidebar is open
      }

      // Check for duplicates based on content, author, and timestamp (within 2 seconds)
      setFloatingMessages((prev) => {
        console.log('[ScreenShareModal] Current floating messages:', prev)

        const isDuplicate = prev.some((existing) => {
          const timeDiff = Math.abs(existing.createdAt - message.createdAt)
          const contentMatch = existing.content === message.content
          const authorMatch = existing.author === message.author
          const timeMatch = timeDiff < 2000

          console.log('[ScreenShareModal] Checking duplicate:', {
            existing,
            message,
            timeDiff,
            contentMatch,
            authorMatch,
            timeMatch,
            isDupe: contentMatch && authorMatch && timeMatch,
          })

          return contentMatch && authorMatch && timeMatch
        })

        if (isDuplicate) {
          console.log('[ScreenShareModal] Skipping duplicate floating message:', message)
          return prev
        }

        const floatingMessage: FloatingMessage = {
          ...message,
          id: `${message.createdAt}-${Math.random()}`,
          dismissed: false,
        }

        console.log('[ScreenShareModal] Adding floating message:', floatingMessage)

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          console.log('[ScreenShareModal] Auto-dismissing floating message:', floatingMessage.id)
          dismissFloatingMessage(floatingMessage.id)
        }, 5000)

        return [...prev, floatingMessage]
      })
    },
    [showChat, dismissFloatingMessage]
  )

  // Memoize filterMessages to prevent recreation on every render
  const filterMessages = React.useCallback(
    (
      messages: ChatMessage[],
      sessionId: string,
      participantsList: any[],
      isInSameRoom: (id: string) => boolean,
      colyseusRoom: ColyseusRoomForChatResolve,
      liveKitIds: Set<string>
    ): ChatMessage[] => {
      return messages.filter((msg) => {
        if (msg.roomId !== sessionId) return false

        const authorSid = resolveAuthorSessionIdFromMessage(msg, colyseusRoom)
        if (!authorSid) return true

        const mySid = colyseusRoom.sessionId
        const schemaMe = colyseusRoom.state?.players?.get(mySid) as { serverVirtualRoomId?: string } | undefined
        const schemaOther = colyseusRoom.state?.players?.get(authorSid) as
          | { serverVirtualRoomId?: string }
          | undefined
        const serverSameCell =
          !!schemaMe?.serverVirtualRoomId &&
          schemaMe.serverVirtualRoomId.length > 0 &&
          schemaMe.serverVirtualRoomId === schemaOther?.serverVirtualRoomId
        if (serverSameCell) return true

        const senderParticipant = participantsList.find((p) => p.identity === authorSid)
        if (senderParticipant?.isLocal) return true

        if (senderParticipant && !senderParticipant.isLocal) {
          if (!isInSameRoom(authorSid) && !liveKitIds.has(authorSid)) return false
          return true
        }

        // Sender not in proximity-filtered list but still in this LiveKit room
        if (liveKitIds.has(authorSid)) return true

        return true
      })
    },
    []
  )

  // Keep chat handler refs up to date so the single listener always sees current values
  useEffect(() => {
    chatHandlerRefs.current = {
      screenshareSessionId,
      participants,
      liveKitIdentities,
      isParticipantInSameRoom,
      addFloatingMessage,
      setChatMessages,
      filterMessages,
    }
  }, [
    screenshareSessionId,
    participants,
    liveKitIdentities,
    isParticipantInSameRoom,
    addFloatingMessage,
    filterMessages,
  ])

  // One-time sync from Colyseus state when session changes
  const lastSyncedSessionRef = useRef<string>('')
  useEffect(() => {
    if (!room || !screenshareSessionId) return
    if (lastSyncedSessionRef.current !== screenshareSessionId) {
      lastSyncedSessionRef.current = screenshareSessionId
      const roomChatMsgs = (room.state as any)?.chatMessages
      if (roomChatMsgs) {
        const allMessages = Array.from(roomChatMsgs) as ChatMessage[]
        const filtered = filterMessages(
          allMessages,
          screenshareSessionId,
          participants,
          isParticipantInSameRoom,
          room,
          liveKitIdentities
        )
        screenshareChatStore.setMessages(screenshareSessionId, filtered)
        setChatMessages(filtered)
        console.log(`[ScreenShareModal] Synced ${filtered.length} messages from state for session ${screenshareSessionId}`)
      }
    }
  }, [room, screenshareSessionId, filterMessages, participants, isParticipantInSameRoom, liveKitIdentities])

  // Register chat message listener ONCE when room is available.
  // Colyseus adds listeners (does not replace), so we must not re-register on deps change.
  // Handler reads from chatHandlerRefs for current values.
  useEffect(() => {
    if (!room) return

    const handleBroadcast = (item: ChatMessage) => {
      const refs = chatHandlerRefs.current
      if (!refs) return
      if (item.roomId !== refs.screenshareSessionId) return

      const authorSid = resolveAuthorSessionIdFromMessage(item, room)
      const localPlayer = room.state?.players?.get(room.sessionId)
      const localUserId = (localPlayer as any)?.userId || ''
      const isLocalMessage =
        (item.authorSessionId && item.authorSessionId === room.sessionId) ||
        (!item.authorSessionId &&
          (item.authorId === room.sessionId || (localUserId && item.authorId === localUserId)))

      if (isLocalMessage) {
        return
      }

      if (authorSid) {
        const mySid = room.sessionId
        const schemaMe = room.state?.players?.get(mySid) as { serverVirtualRoomId?: string } | undefined
        const schemaOther = room.state?.players?.get(authorSid) as
          | { serverVirtualRoomId?: string }
          | undefined
        const serverSameCell =
          !!schemaMe?.serverVirtualRoomId &&
          schemaMe.serverVirtualRoomId.length > 0 &&
          schemaMe.serverVirtualRoomId === schemaOther?.serverVirtualRoomId
        if (!serverSameCell) {
          const inProximity = refs.isParticipantInSameRoom(authorSid)
          const inCall = refs.liveKitIdentities.has(authorSid)
          if (!inProximity && !inCall) {
            return
          }
        }
      }

      const added = screenshareChatStore.addMessage(refs.screenshareSessionId, item)
      if (added) {
        refs.setChatMessages(screenshareChatStore.getMessages(refs.screenshareSessionId))
        refs.addFloatingMessage(item)
      }
    }

    room.onMessage(Message.ADD_CHAT_MESSAGE, handleBroadcast)
  }, [room])

  // Clear floating messages when sidebar opens
  useEffect(() => {
    if (showChat) {
      setFloatingMessages([])
    }
  }, [showChat, dismissFloatingMessage])

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight
    }
  }, [chatMessages])

  // Handle chat message send
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    const val = chatInput.trim()
    if (val && room && screenshareSessionId) {
      console.log(`[ScreenShareModal] Sending message: "${val}" to screenshare session ${screenshareSessionId}`)

      room.send(Message.ADD_CHAT_MESSAGE, {
        content: val,
        roomId: screenshareSessionId, // Use screenshare session ID
      })

      // Optimistically add message to the UI and persist to store
      const player = room.state.players.get(room.sessionId)
      const newMessage: ChatMessage = {
        author: player?.name || 'You',
        content: val,
        createdAt: Date.now(),
        roomId: screenshareSessionId,
        authorId: player?.userId || room.sessionId,
        authorSessionId: room.sessionId,
      }

      screenshareChatStore.addMessage(screenshareSessionId, newMessage)
      setChatMessages(screenshareChatStore.getMessages(screenshareSessionId))

      // Show floating message for our own message if sidebar is closed
      console.log(
        '[ScreenShareModal] Checking if should show floating for own message, showChat:',
        showChat
      )
      if (!showChat) {
        console.log('[ScreenShareModal] Adding floating message for own message:', newMessage)
        addFloatingMessage(newMessage)
      }

      setChatInput('')
    } else if (val && !room) {
      dispatch(addNotification({ type: 'warning', message: 'Connection not ready yet. Please try again in a moment.', duration: 4000 }))
    } else if (!screenshareSessionId) {
      console.warn('[ScreenShareModal] Cannot send message: screenshare session ID not set')
    }
  }

  // Toggle chat sidebar
  const toggleChatSidebar = () => {
    setShowChat((prev) => !prev)
  }

  // Auto-focus chat input when sidebar opens
  useEffect(() => {
    if (showChat && chatInputRef.current) {
      const timer = setTimeout(() => chatInputRef.current?.focus(), 0)
      return () => clearTimeout(timer)
    }
  }, [showChat])

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setChatInput((prev) => prev + emoji)
    // Keep emoji picker open for multiple selections
    // setShowEmojiPicker(false)

    // Focus back to input after emoji selection
    if (chatInputRef.current) {
      chatInputRef.current.focus()
    }
  }

  // Toggle emoji picker
  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev)
  }

  // Toggle participants drawer
  const toggleParticipantsDrawer = () => {
    setShowParticipantsDrawer((prev) => !prev)
  }

  // Get participant initials for avatar
  const getParticipantInitials = (name: string): string => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase()
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
  }

  // Handle switching to the new screen share
  const handleSwitchToNewScreenShare = () => {
    if (newScreenShare && onSwitchScreenShare) {
      onSwitchScreenShare(newScreenShare.participantId, newScreenShare.trackSid, screenshareSessionId)
      setShowTakeoverNotification(false)
      setNewScreenShare(null)
    }
  }

  // Handle dismissing the takeover notification
  const handleDismissTakeoverNotification = () => {
    setShowTakeoverNotification(false)
  }

  // Request to take over screen sharing from the current presenter
  const handleRequestTakeover = () => {
    if (!room || takeoverRequestPending || isLocalSharing) return
    setTakeoverRequestPending(true)
    setTakeoverResultMessage(null)
    room.send(Message.REQUEST_SCREEN_SHARE_TAKEOVER, {
      presenterUserId: maximizedScreenShare.participantId,
    })
  }

  // Presenter accepts an incoming takeover request
  const handleAcceptTakeover = () => {
    if (!room || !incomingTakeoverRequest) return
    room.send(Message.SCREEN_SHARE_TAKEOVER_RESPONSE, { accepted: true })
    setIncomingTakeoverRequest(null)
    // Stop our own screen share after a brief delay so the response is sent first
    setTimeout(() => onToggleScreenShare(), 500)
  }

  // Presenter declines an incoming takeover request
  const handleDeclineTakeover = () => {
    if (!room || !incomingTakeoverRequest) return
    room.send(Message.SCREEN_SHARE_TAKEOVER_RESPONSE, { accepted: false })
    setIncomingTakeoverRequest(null)
  }

  // Auto-decline incoming takeover request after 30 seconds
  useEffect(() => {
    if (!incomingTakeoverRequest || !room) return
    const timer = setTimeout(() => {
      room.send(Message.SCREEN_SHARE_TAKEOVER_RESPONSE, { accepted: false })
      setIncomingTakeoverRequest(null)
    }, 30000)
    return () => clearTimeout(timer)
  }, [incomingTakeoverRequest, room])

  // Close the screen share source menu when clicking outside of it
  useEffect(() => {
    if (!showScreenShareMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (screenShareMenuRef.current && !screenShareMenuRef.current.contains(e.target as Node)) {
        setShowScreenShareMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showScreenShareMenu])

  // Handle "Change Source" — stop current share and re-open picker without closing modal
  const handleChangeSource = useCallback(() => {
    setShowScreenShareMenu(false)
    // Mark that we are in a source-change cycle so auto-close is suppressed
    isChangingSourceRef.current = true
    // Clear the flag after 3 s — well past the 600 ms restart in useVideoDisplayState
    setTimeout(() => { isChangingSourceRef.current = false }, 3000)
    if (onChangeScreenShareSource) {
      onChangeScreenShareSource()
    } else {
      // Fallback: just toggle off (old behaviour)
      onToggleScreenShare()
    }
  }, [onChangeScreenShareSource, onToggleScreenShare])

  // Handle "Stop Sharing" — stop the current share and close the menu
  const handleStopSharing = useCallback(() => {
    setShowScreenShareMenu(false)
    onToggleScreenShare()
  }, [onToggleScreenShare])

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showEmojiPicker) return

      const target = event.target as Node
      const emojiPicker = document.querySelector('[data-emoji-picker]')
      const emojiButton = emojiButtonRef.current

      // Don't close if clicking on the emoji picker itself or the emoji button
      if (
        (emojiPicker && emojiPicker.contains(target)) ||
        (emojiButton && emojiButton.contains(target)) ||
        (chatInputRef.current && chatInputRef.current.contains(target))
      ) {
        return
      }

      // Close the picker if clicking outside
      setShowEmojiPicker(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  // Close reaction bar when clicking outside — keep it open across multiple picks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showReactionBar) return

      const target = event.target as Node
      const reactionBar = document.querySelector('[data-reaction-bar]')
      const reactionButton = reactionButtonRef.current

      // Don't close if clicking on the reaction bar itself or its toggle button
      if (
        (reactionBar && reactionBar.contains(target)) ||
        (reactionButton && reactionButton.contains(target))
      ) {
        return
      }

      setShowReactionBar(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showReactionBar])

  // Lock game inputs when modal opens (use MODAL so game input is disabled) and unlock when it closes
  useEffect(() => {
    inputController.lockInput(InputLockReason.MODAL)
    phaserEvents.emit('chat-opened')

    return () => {
      inputController.unlockInput(InputLockReason.MODAL)
      inputController.unlockInput(InputLockReason.CHAT_FOCUSED)
      phaserEvents.emit('chat-closed')

      const gameCanvas = document.querySelector('canvas')
      if (gameCanvas) {
        gameCanvas.focus()
      }
    }
  }, [])

  // Ref to hold latest state for the Escape handler so the listener doesn't
  // need to re-register on every participants / screenShareEnabled change.
  // Assigned synchronously during render so the handler never sees stale values.
  const escCloseStateRef = useRef({ screenShareEnabled, isLocalSharing, participants, maximizedScreenShare, incomingTakeoverRequest, room })
  escCloseStateRef.current = { screenShareEnabled, isLocalSharing, participants, maximizedScreenShare, incomingTakeoverRequest, room }

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()

        const state = escCloseStateRef.current
        const actions = getEscapeCleanupActions(state)

        // Decline any pending takeover request so the requester isn't stuck.
        if (actions.declineTakeoverRequest) {
          (state.room as any).send(Message.SCREEN_SHARE_TAKEOVER_RESPONSE, { accepted: false })
        }

        // Resync the screen-share button when the share already ended.
        if (actions.resetScreenShareButton) {
          window.dispatchEvent(
            new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, { detail: { enabled: false } })
          )
        }

        onClose()
      }
    }

    window.addEventListener('keydown', handleEscKey, { capture: true })

    return () => {
      window.removeEventListener('keydown', handleEscKey, { capture: true })
    }
  }, [onClose])

  // Global key capture to prevent game inputs and allow normal typing.
  // Registers once — uses blockedKeysRef (not state) so listeners are stable.
  useEffect(() => {
    const keysToBlock = [
      'w',
      'a',
      's',
      'd',
      'e',
      'r',
      'q',
      't',
      'f',
      'g',
      'h',
      'c',
      'v',
      'b',
      'n',
      'm',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      ' ',
      'Tab',
      'Shift',
      'Control',
      'Alt',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '0',
    ]

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()

      // Check if we're in an emoji picker or other chat UI element
      const target = e.target as HTMLElement
      const isInEmojiPicker = target.closest('[data-testid="emoji-picker"]') ||
        target.closest('.EmojiMart') ||
        target.closest('[role="dialog"]')

      // If it's a key we want to block and the focus is in our chat component
      if (keysToBlock.includes(key) || keysToBlock.includes(e.key)) {
        // Block if focused on chat input, search input, or emoji picker
        if (document.activeElement === chatInputRef.current ||
          isInEmojiPicker) {
          e.stopPropagation()
          blockedKeysRef.current.add(key)
        }
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()

      // If this was a key we previously blocked
      if (blockedKeysRef.current.has(key)) {
        blockedKeysRef.current.delete(key)
      }
    }

    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()

      // Check if we're in an emoji picker or other chat UI element
      const target = e.target as HTMLElement
      const isInEmojiPicker = target.closest('[data-testid="emoji-picker"]') ||
        target.closest('.EmojiMart') ||
        target.closest('[role="dialog"]')

      // If it's a key we want to block and the focus is in our chat component
      if (keysToBlock.includes(key) || keysToBlock.includes(e.key)) {
        // Block if focused on chat input, search input, or emoji picker
        if (document.activeElement === chatInputRef.current ||
          isInEmojiPicker) {
          e.stopPropagation()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown, { capture: true })
    window.addEventListener('keyup', handleKeyUp, { capture: true })
    window.addEventListener('keypress', handleKeyPress, { capture: true })

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
      window.removeEventListener('keyup', handleKeyUp, { capture: true })
      window.removeEventListener('keypress', handleKeyPress, { capture: true })
    }
  }, [])

  // Handle chat input focus - use CHAT_FOCUSED so game keyboard is disabled while typing
  const handleChatInputFocus = () => {
    inputController.lockInput(InputLockReason.CHAT_FOCUSED)
  }

  const handleChatInputBlur = () => {
    inputController.unlockInput(InputLockReason.CHAT_FOCUSED)
  }

  // Add keyboard event handlers for chat input
  const handleChatInputKeyEvents = (e: React.KeyboardEvent) => {
    e.stopPropagation()
  }

  // SC-209: the call bar's button, decided in one place. Safari with nothing attached is `blocked`: a click
  // could only fail silently, so it is drawn disabled with the reason.
  const pipButton = pipButtonState(pictureInPicture)
  const showPipButton = pipButton.show
  const pipBlocked = pipButton.blocked
  const pipLabel = pipButton.label

  return (
    <>
      <KeyboardCaptureLayer />

      <MaximizedScreenShareContainer>
        <MaximizedScreenShareContent>
          {/* Chat Sidebar */}
          {showChat && (
            <ChatSidebarComponent
              messages={chatMessages}
              inputValue={chatInput}
              disabled={!screenshareSessionId}
              showEmojiPicker={showEmojiPicker}
              onChangeInput={setChatInput}
              onSubmit={handleSendMessage}
              onToggleEmojiPicker={toggleEmojiPicker}
              onEmojiSelect={handleEmojiSelect}
              messagesRef={chatMessagesRef}
              inputRef={chatInputRef}
              emojiButtonRef={emojiButtonRef}
              onInputFocus={handleChatInputFocus}
              onInputBlur={handleChatInputBlur}
              onClose={toggleChatSidebar}
            />
          )}

          <ScreenShareView>
            {/* Floating Message Bubbles */}
            {!showChat && floatingMessages.length > 0 && (
              <FloatingMessages messages={floatingMessages} onDismiss={dismissFloatingMessage} />
            )}

            {/* Reaction overlay */}
            <ReactionOverlay reactions={reactions} />

            {(() => {
              // Check participant state first
              const participant = participants.find(p => p.identity === maximizedScreenShare.participantId)
              const isParticipantScreenSharing = participant?.isScreenShareEnabled ?? false

              if (screenShareTrack) {
                // Track is available - render it
                return React.createElement(VideoTrack as any, { trackRef: screenShareTrack })
              } else if (isParticipantScreenSharing) {
                // Participant is screen sharing but track not available yet - show loading state
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    <div>📺</div>
                    <div>Loading screen share...</div>
                    <div style={{ fontSize: '14px', opacity: 0.7 }}>
                      Please wait while the screen share initializes
                    </div>
                  </div>
                )
              } else {
                // Participant stopped screen sharing - show ended message
                return (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    <div>📺</div>
                    <div>Screen share ended</div>
                    <div style={{ fontSize: '14px', opacity: 0.7 }}>
                      This window will close automatically...
                    </div>
                  </div>
                )
              }
            })()}
          </ScreenShareView>

          {showParticipants && (
            <ParticipantsPanel
              sortedCameraTracks={sortedCameraTracksWithSid}
              groupedCameraTracks={groupedCameraTracksWithSid}
              allParticipants={participants}
              cameraTracksByParticipantId={cameraTracksByParticipantId}
              speakingIds={speakingIds}
              handRaisedSessionIds={handRaisedSessionIds}
              screenShareParticipantId={maximizedScreenShare.participantId}
              cameraViewMode={
                cameraViewMode === CameraViewMode.SINGLE_COLUMN ? 'single' : 'conference'
              }
              audioEnabled={audioEnabled}
              videoEnabled={videoEnabled}
              toggleAudio={toggleAudio}
              toggleVideo={toggleVideo}
              room={room}
              ownerSessionId={ownerSessionId}
              isLoading={!isParticipantsListLoaded}
            />
          )}

          {/* Toggle participants panel button */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: showParticipants
                ? cameraViewMode === CameraViewMode.CONFERENCE
                  ? '408px'
                  : '308px'
                : '8px',
              transform: 'translateY(-50%)',
              zIndex: 20,
            }}
          >
            <VideoControlButton
              onClick={toggleParticipantsPanel}
              aria-label={showParticipants ? 'Hide participants' : 'Show participants'}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                width: '32px',
                height: '32px',
              }}
            >
              {showParticipants
                ? React.createElement(BsChevronRight as any)
                : React.createElement(BsPeopleFill as any)}
            </VideoControlButton>
          </div>
        </MaximizedScreenShareContent>

        {/* Title showing who is sharing */}
        <ScreenShareTitle>
          <DisplayIcon size={16} />{' '}
          {screenShareTrack ? `${sharerName}'s Screen` : 'Screen Share Ended'}
        </ScreenShareTitle>

        {/* Takeover notification for new screen shares */}
        {newScreenShare && (
          <TakeoverNotification show={showTakeoverNotification}>
            <TakeoverNotificationContent>
              <TakeoverNotificationTitle>
                <DisplayIcon size={14} />
                New Screen Share Available
              </TakeoverNotificationTitle>
              <TakeoverNotificationText>
                {newScreenShare.participantName} started sharing their screen
              </TakeoverNotificationText>
            </TakeoverNotificationContent>
            <TakeoverNotificationButton onClick={handleSwitchToNewScreenShare}>
              Switch
            </TakeoverNotificationButton>
            <TakeoverNotificationClose onClick={handleDismissTakeoverNotification}>
              {React.createElement(BsX as any, { size: 18 })}
            </TakeoverNotificationClose>
          </TakeoverNotification>
        )}

        {/* Incoming takeover request notification (shown to presenter) */}
        {incomingTakeoverRequest && (
          <TakeoverNotification show={true}>
            <TakeoverNotificationContent>
              <TakeoverNotificationTitle>
                <DisplayIcon size={14} />
                Takeover Request
              </TakeoverNotificationTitle>
              <TakeoverNotificationText>
                {incomingTakeoverRequest.requesterName} wants to take over presenting
              </TakeoverNotificationText>
            </TakeoverNotificationContent>
            <TakeoverNotificationButton onClick={handleAcceptTakeover}>
              Accept
            </TakeoverNotificationButton>
            <TakeoverNotificationButton
              onClick={handleDeclineTakeover}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
            >
              Decline
            </TakeoverNotificationButton>
            <TakeoverNotificationClose onClick={handleDeclineTakeover}>
              {React.createElement(BsX as any, { size: 18 })}
            </TakeoverNotificationClose>
          </TakeoverNotification>
        )}

        {/* Takeover result feedback (shown to requester) */}
        {takeoverResultMessage && (
          <TakeoverNotification show={true}>
            <TakeoverNotificationContent>
              <TakeoverNotificationText style={{ fontSize: '14px' }}>
                {takeoverResultMessage}
              </TakeoverNotificationText>
            </TakeoverNotificationContent>
            <TakeoverNotificationClose onClick={() => setTakeoverResultMessage(null)}>
              {React.createElement(BsX as any, { size: 18 })}
            </TakeoverNotificationClose>
          </TakeoverNotification>
        )}

        {/* Close button in top right */}
        <CloseButton onClick={onClose} aria-label="Close screen share">
          {React.createElement(BsX as any, { size: 20 })}
        </CloseButton>

        {/* Reaction bar — floats above the bottom-right controls, never occluded by them */}
        {showReactionBar && (
          <ReactionBar
            onReact={(emoji) => addReaction(emoji)}
            style={{
              position: 'absolute',
              bottom: '88px',
              right: '24px',
              background: 'rgba(20, 20, 20, 0.82)',
              backdropFilter: 'blur(10px)',
              borderRadius: '32px',
              padding: '8px 14px',
              gap: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.1)',
              zIndex: 50,
            }}
          />
        )}

        {/* Controls in bottom right */}
        <MaximizedScreenShareControls>
          <ChatToggleButton
            onClick={toggleChatSidebar}
            aria-label={showChat ? 'Hide chat' : 'Show chat'}
            title={showChat ? 'Hide chat' : 'Show chat'}
            data-testid="chat-toggle-button"
          >
            {React.createElement(BsChatDots as any, { size: 18 })}
          </ChatToggleButton>
          {/* Reactions toggle button — sits next to mute audio/video */}
          <VideoControlButton
            ref={reactionButtonRef}
            onClick={() => setShowReactionBar((prev) => !prev)}
            aria-label={showReactionBar ? 'Hide reactions' : 'Show reactions'}
            title={showReactionBar ? 'Hide reactions' : 'Show reactions'}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: showReactionBar ? 'color-mix(in srgb, var(--sevv-primary, #f97415) 85%, transparent)' : 'rgba(0, 0, 0, 0.6)',
              border: showReactionBar ? '2px solid color-mix(in srgb, var(--sevv-primary, #f97415) 100%, transparent)' : 'none',
              fontSize: '20px',
            }}
          >
            😊
          </VideoControlButton>
          <MediaControlGroup>
            <VideoControlButton
              active={audioEnabled}
              muted={!audioEnabled}
              onClick={toggleAudio}
              aria-label={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
              title={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
              style={{
                width: '40px',
                height: '40px',
              }}
            >
              {audioEnabled ? <MicOnIcon /> : <MicOffIcon />}
            </VideoControlButton>
            <DeviceSelectMenu kind="audioinput" />
          </MediaControlGroup>

          <MediaControlGroup>
            <VideoControlButton
              active={videoEnabled}
              muted={!videoEnabled}
              onClick={toggleVideo}
              disabled={cameraDisabled}
              aria-label={cameraLabel}
              title={cameraLabel}
              style={{
                width: '40px',
                height: '40px',
              }}
            >
              {videoEnabled ? <CameraOnIcon /> : <CameraOffIcon />}
            </VideoControlButton>
            <DeviceSelectMenu kind="videoinput" />
          </MediaControlGroup>

          {canRaiseHand && (
            <RaiseHandButton raised={localHandRaised} onToggle={toggleHandRaise} disabled={!room} />
          )}

          {/* SC-209: this view covers the call bar, so its pop-out button would be out of reach. Same icon
              and words as the call bar's, so it reads as the same action. Popping out never changes the
              tab: the share stays maximised here. */}
          {showPipButton &&
            (pipBlocked ? (
              // A disabled button gets no mouse events in some browsers, so the reason hangs off a live
              // wrapper and the button lets the pointer through to it.
              <span
                title={pipLabel}
                data-testid="screen-share-pip-blocked"
                style={{ display: 'inline-flex' }}
              >
                <VideoControlButton
                  type="button"
                  disabled
                  aria-disabled="true"
                  aria-label={pipLabel}
                  aria-pressed={false}
                  data-testid="screen-share-pip-button"
                  data-pip-state="unavailable"
                  style={{
                    width: '40px',
                    height: '40px',
                    pointerEvents: 'none',
                  }}
                >
                  <PictureInPictureAlt fontSize="small" />
                </VideoControlButton>
              </span>
            ) : (
              <VideoControlButton
                type="button"
                onClick={pipButton.toggle}
                aria-label={pipLabel}
                aria-pressed={pictureInPicture.isOpen}
                title={pipLabel}
                data-testid="screen-share-pip-button"
                data-pip-state={pictureInPicture.isOpen ? 'open' : 'closed'}
                style={{
                  width: '40px',
                  height: '40px',
                  ...(pictureInPicture.isOpen
                    ? { backgroundColor: SEVV_PRIMARY_CSS, border: `2px solid ${SEVV_PRIMARY_CSS}` }
                    : {}),
                }}
              >
                <PictureInPictureAlt fontSize="small" />
              </VideoControlButton>
            ))}

          {showParticipants && (
            <VideoControlButton
              onClick={toggleCameraViewMode}
              aria-label={`Switch to ${cameraViewMode === CameraViewMode.SINGLE_COLUMN ? 'conference' : 'single column'
                } view`}
              title={`Switch to ${cameraViewMode === CameraViewMode.SINGLE_COLUMN ? 'conference' : 'single column'
                } view`}
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(13, 110, 253, 0.9)',
                border: '2px solid rgba(13, 110, 253, 1)',
                boxShadow: '0 4px 12px rgba(13, 110, 253, 0.4)',
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              {cameraViewMode === CameraViewMode.SINGLE_COLUMN ? '2×2' : '1×1'}
            </VideoControlButton>
          )}

          {isLocalSharing && (
            <div ref={screenShareMenuRef} style={{ position: 'relative' }}>
              {/* Change Source / Stop Sharing dropdown */}
              <ScreenShareMenu open={showScreenShareMenu}>
                <ScreenShareMenuItem onClick={handleChangeSource} title="Stop sharing and open a new source picker">
                  <DisplayIcon size={14} />
                  Change Source
                </ScreenShareMenuItem>
                <ScreenShareMenuItem danger onClick={handleStopSharing} title="Stop screen sharing">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {React.createElement(BsX as any, { size: 16 })}
                  </span>
                  Stop Sharing
                </ScreenShareMenuItem>
              </ScreenShareMenu>

              {/* Presenter button – opens the menu instead of immediately stopping */}
              <VideoControlButton
                active={screenShareEnabled}
                onClick={() => setShowScreenShareMenu(prev => !prev)}
                aria-label={showScreenShareMenu ? 'Close screen share options' : 'Screen share options'}
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: showScreenShareMenu
                    ? 'rgba(220, 53, 69, 1)'
                    : 'rgba(220, 53, 69, 0.9)',
                  border: '2px solid rgba(220, 53, 69, 1)',
                  boxShadow: showScreenShareMenu
                    ? '0 4px 20px rgba(220, 53, 69, 0.7)'
                    : '0 4px 12px rgba(220, 53, 69, 0.4)',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
              >
                <DisplayIcon size={18} />
              </VideoControlButton>
            </div>
          )}

          {/* Request to Present button (only for viewers, not the presenter) */}
          {!isLocalSharing && !screenShareEnabled && (
            <ExitFullscreenButton
              onClick={handleRequestTakeover}
              disabled={takeoverRequestPending}
              aria-label="Request to present"
              style={{
                backgroundColor: takeoverRequestPending
                  ? 'rgba(100, 100, 100, 0.6)'
                  : 'color-mix(in srgb, var(--sevv-primary, #f97415) 90%, transparent)',
                cursor: takeoverRequestPending ? 'not-allowed' : 'pointer',
                opacity: takeoverRequestPending ? 0.7 : 1,
              }}
            >
              <DisplayIcon size={14} />
              {takeoverRequestPending ? 'Request Sent...' : 'Request to Present'}
            </ExitFullscreenButton>
          )}

          {/* Participant count display */}
          <ParticipantCountDisplay
            onClick={toggleParticipantsDrawer}
            aria-label="Show participants list"
            disabled={!isParticipantsListLoaded}
          >
            <ParticipantCountIcon>
              {React.createElement(BsPeopleFill as any, { size: 14 })}
            </ParticipantCountIcon>
            <ParticipantCountText>
              {isParticipantsListLoaded ? (
                `${sharingContextParticipantCount} ${sharingContextParticipantCount === 1 ? 'participant' : 'participants'}`
              ) : (
                'Loading...'
              )}
            </ParticipantCountText>
          </ParticipantCountDisplay>

          {/* Participants list drawer */}
          <ParticipantsDrawer open={showParticipantsDrawer}>
            <ParticipantsDrawerHeader>
              <ParticipantsDrawerTitle>
                {React.createElement(BsPeopleFill as any, { size: 16 })}
                Participants ({isParticipantsListLoaded ? sharingContextParticipantCount : '...'})
              </ParticipantsDrawerTitle>
              <ParticipantsDrawerClose
                onClick={toggleParticipantsDrawer}
                aria-label="Close participants list"
              >
                {React.createElement(BsX as any, { size: 20 })}
              </ParticipantsDrawerClose>
            </ParticipantsDrawerHeader>
            <ParticipantsDrawerList>
              {!isParticipantsListLoaded ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', gap: '12px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid rgba(0, 0, 0, 0.05)',
                    borderTop: '3px solid var(--sevv-primary, #f97415)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                  <span style={{ fontSize: '13px', color: 'var(--sevv-text-secondary, #757575)', fontWeight: 500 }}>Syncing...</span>
                </div>
              ) : (
                sharingContextParticipants.map((participant) => {
                const isLocal = participant.isLocal
                const isSpeaking = speakingIds.has(participant.identity)

                return (
                  <ParticipantItem
                    key={participant.identity}
                    isLocal={isLocal}
                    isSpeaking={isSpeaking}
                  >
                    <ParticipantAvatar isLocal={isLocal}>
                      {getParticipantInitials(participant.name || participant.identity)}
                    </ParticipantAvatar>
                    <ParticipantInfo>
                      <ParticipantNameText data-testid={`drawer-participant-name-${participant.identity}`}>
                        {participant.name || participant.identity}
                      </ParticipantNameText>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {isLocal && <ParticipantBadge type="local">You</ParticipantBadge>}
                        {ownerSessionId && participant.identity === ownerSessionId && (
                          <ParticipantBadge type="owner">Owner</ParticipantBadge>
                        )}
                        {isSpeaking && <ParticipantBadge type="speaking">Speaking</ParticipantBadge>}
                        {(() => {
                          const playerSchema = room?.state?.players?.get(participant.identity)
                          const hasHandRaised = playerSchema?.handRaised ?? false
                          if (!hasHandRaised) return null

                          const canLower = isLocalOwner || isLocalPresenter
                          return (
                            <ParticipantBadge
                              type="hand-raised"
                              {...handBadgeProps(canLower ? () => lowerHand(participant.identity) : undefined)}
                              data-testid={`participant-drawer-hand-${participant.identity}`}
                            >
                              ✋ Hand Raised
                            </ParticipantBadge>
                          )
                        })()}
                      </div>
                    </ParticipantInfo>
                  </ParticipantItem>
                )
              }) )}
            </ParticipantsDrawerList>
          </ParticipantsDrawer>

          <ExitFullscreenButton onClick={onClose} aria-label="Exit fullscreen">
            {React.createElement(BsFullscreenExit as any, { size: 16 })} Exit Fullscreen
          </ExitFullscreenButton>
        </MaximizedScreenShareControls>

      </MaximizedScreenShareContainer>
    </>
  )
}

export default ScreenShareModal
